import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import BynderResizeImage from '@/components/atoms/BynderResizeImage/BynderResizeImage';
import BynderVideo from '@/components/atoms/BynderVideo/BynderVideo';
import PodcastEmbed from '@/components/atoms/PodcastEmbed/PodcastEmbed';
import { cn } from '@/utils/classUtils';
import React, { useImperativeHandle, useRef } from 'react';
import { Boundary } from '../Boundary/Boundary';
import BynderAudio from '../BynderAudio/BynderAudio';
const isBynderImageAsset = (item) => item?.contentType === 'bynderCmsImageAsset';
const isBynderVideoAsset = (item) => item?.contentType === 'bynderCmsVideoAsset';
const isBynderAudioAsset = (item) => item?.contentType === 'bynderCmsAudioAsset';
const isVideoMedia = (item) => item?.contentType === 'videoMedia';
const isPodcastMedia = (item) => item?.contentType === 'podcastMedia';
/**
 * MediaBlocks is a small resolver for handling media rendering inside content pages.
 * It supports Bynder images, videos, audio, and podcast embeds out of the box.
 *
 * When assigned a ref, it will return a custom instance object with the following properties:
 * - `ref.current.image`: HTMLImageElement for Bynder images
 * - `ref.current.video`: HTMLVideoElement for Bynder videos
 * - `ref.current.audio`: HTMLAudioElement for Bynder audios
 */
const MediaBlocks = React.forwardRef(({ mediaItem, imageProps, videoProps, audioProps, podcastProps }, forwardedRef) => {
    const imageRef = useRef(null);
    const videoRef = useRef(null);
    const audioRef = useRef(null);
    useImperativeHandle(forwardedRef, () => ({
        image: imageRef.current,
        video: videoRef.current,
        audio: audioRef.current,
    }));
    // Fallback shown when no media item is provided.
    // This exists because the CMS cannot enforce a mandatory image/video/audio for this block,
    // as the same component is reused across multiple contexts.
    // The purpose is to alert content authors during editing so they can fix missing media
    // before publishing. It should never appear on the live site.
    if (!mediaItem) {
        return (_jsx(Boundary, { children: _jsxs("div", { className: cn('flex aspect-video w-full items-center justify-center overflow-hidden rounded-lg bg-black p-4 text-center text-white lg:rounded-2xl', imageProps?.className), children: [_jsx("span", { "aria-label": "Warning", role: "img", children: "\u26A0\uFE0F" }), ' ', "No media selected. Please add a media item."] }) }));
    }
    if (isBynderImageAsset(mediaItem)) {
        return (_jsx(Boundary, { children: _jsx(BynderResizeImage, { ref: imageRef, image: mediaItem, ...imageProps, className: cn('size-full rounded-lg lg:rounded-2xl', imageProps?.className) }) }));
    }
    if ((isVideoMedia(mediaItem) && mediaItem.content.video) || isBynderVideoAsset(mediaItem)) {
        const video = isVideoMedia(mediaItem) ? mediaItem.content.video : mediaItem;
        return (_jsx(Boundary, { children: _jsx(BynderVideo, { ref: videoRef, video: video, autoPlay: false, mobileFullscreen: true, ...videoProps, className: cn(videoProps?.className) }) }));
    }
    if (isBynderAudioAsset(mediaItem)) {
        return (_jsx(Boundary, { children: _jsx(BynderAudio, { ref: audioRef, audio: mediaItem, ...audioProps }) }));
    }
    if (isPodcastMedia(mediaItem)) {
        const { podcast, externalPodcastAsset } = mediaItem.content;
        if (podcast && isBynderAudioAsset(podcast)) {
            return (_jsx(Boundary, { children: _jsx(BynderAudio, { ref: audioRef, audio: podcast, ...audioProps }) }));
        }
        if (externalPodcastAsset) {
            return (_jsx(Boundary, { children: _jsx(PodcastEmbed, { podcast: mediaItem, ...podcastProps }) }));
        }
    }
    console.warn(`Unknown media type: ${mediaItem.contentType}`);
    return null;
});
MediaBlocks.displayName = 'MediaBlocks';
export default MediaBlocks;
